import { Controller } from "@hotwired/stimulus"
import { escapeHtml as _escapeHtml } from "../lib/html_sanitizer"
import { valueOrEmptyHtml } from "../lib/empty_value"
import { isMobileSheetViewport, isTouchPrimary, MOBILE_SHEET_BREAKPOINT } from "../lib/mobile_viewport"

// Stamped by mobile_select_summary on any native multi-select it collapses.
// Doubles as the routing marker here: a field whose own classes are not on the
// list below (the licensed-states partial renders `class: "select2"`) would
// otherwise be left with a trigger that opens nothing.
const SHEET_SELECT_CLASS = "gl-mobile-sheet-select"

// The summary trigger lib/mobile_select_summary.js inserts right after a select
// it collapses. It is the field's operable, focusable control (the select
// itself is tabindex -1 and any focus on it is blurred below to keep iOS's
// picker shut), so it is where focus returns when the sheet closes (#14692).
const SUMMARY_TRIGGER_CLASS = "gl-mobile-select-summary"

// Which selects this sheet takes over.
const SELECT_CLASSES = [
  "gl-select-lg",
  "gl-select",
  "gl-mobile-filter-select",
  SHEET_SELECT_CLASS,
]

const DELEGATED_SELECT_SELECTOR = [
  ...SELECT_CLASSES,
  "gl-filter-dropdown-select",
  "form-select",
  "form-control",
].map((className) => `select.${className}`).join(", ")

const SCOPED_SELECT_SELECTOR = SELECT_CLASSES.map((className) => `.${className}`).join(", ")

/**
 * Mobile Dropdown Controller
 *
 * Intercepts native select dropdowns on mobile devices and shows a
 * custom bottom-sheet style overlay with large, readable options.
 *
 * Native mobile <select> dropdowns render options using OS-level styling
 * that cannot be controlled with CSS. This controller provides a mobile-friendly
 * alternative with proper touch targets and readable font sizes.
 *
 * Usage:
 *   <select data-controller="mobile-dropdown" data-mobile-dropdown-title-value="Select Status">
 *     <option value="">All</option>
 *     <option value="new">New</option>
 *   </select>
 *
 * Or wrap multiple selects:
 *   <div data-controller="mobile-dropdown">
 *     <select data-mobile-dropdown-target="select">...</select>
 *     <select data-mobile-dropdown-target="select">...</select>
 *   </div>
 */
export default class extends Controller {
  static targets = ["select"]
  static values = {
    title: { type: String, default: "Select an option" },
    mobileBreakpoint: { type: Number, default: MOBILE_SHEET_BREAKPOINT }
  }

  connect() {
    this.overlay = null
    this.returnFocusTo = null
    this.boundCloseOnEscape = this.closeOnEscape.bind(this)
    this.boundBeforeCache = this.beforeCache.bind(this)
    this.managedSelects = []
    // Turbo snapshots the live DOM for its cache: an open sheet would be
    // frozen into the snapshot (orphaned overlay, body scroll locked) and come
    // back on history navigation with no controller behind it (#14692).
    document.addEventListener("turbo:before-cache", this.boundBeforeCache)

    // If the controller is on a select element itself, use it directly
    if (this.element.tagName === "SELECT") {
      this.setupSelect(this.element)
    } else if (this.element.tagName === "BODY") {
      // Global mode: use event delegation for all selects (including dynamic ones)
      this._globalHandler = (event) => {
        const select = event.target.closest(DELEGATED_SELECT_SELECTOR)
        if (select) this.handleSelectClick(event, select)
      }
      this.element.addEventListener("touchstart", this._globalHandler, { passive: false })
      this.element.addEventListener("mousedown", this._globalHandler)
    } else {
      // Scoped mode: find selects inside this element
      const selects = this.hasSelectTarget
        ? this.selectTargets
        : this.element.querySelectorAll(SCOPED_SELECT_SELECTOR)

      selects.forEach(select => this.setupSelect(select))
    }
  }

  disconnect() {
    this.closeDropdown()
    document.removeEventListener("turbo:before-cache", this.boundBeforeCache)

    // Clean up global delegation handler
    if (this._globalHandler) {
      this.element.removeEventListener("touchstart", this._globalHandler)
      this.element.removeEventListener("mousedown", this._globalHandler)
      this._globalHandler = null
    }

    // Clean up event listeners from all managed selects
    this.managedSelects.forEach(select => {
      if (select._mobileDropdownHandler) {
        select.removeEventListener("mousedown", select._mobileDropdownHandler)
        select.removeEventListener("touchstart", select._mobileDropdownHandler)
        select.removeEventListener("click", select._mobileDropdownHandler)
        select.removeEventListener("focus", select._mobileDropdownHandler)
        delete select._mobileDropdownHandler
      }
    })
    this.managedSelects = []
  }

  setupSelect(select) {
    // Store original click handler reference for cleanup
    const handler = (event) => this.handleSelectClick(event, select)
    select._mobileDropdownHandler = handler

    // Track managed selects for cleanup on disconnect
    this.managedSelects.push(select)

    // Intercept ALL events that could trigger native iOS picker
    // iOS can trigger the picker via click (after touchend), focus, or mousedown
    select.addEventListener("mousedown", handler)
    select.addEventListener("touchstart", handler, { passive: false })
    select.addEventListener("click", handler)  // Critical for iOS - fires after touchend
    select.addEventListener("focus", handler)  // Prevents focus-triggered picker
  }

  handleSelectClick(event, select) {
    if (!this.shouldHandle(select)) return

    // A disabled select is locked by the form that owns it (e.g. the coupon
    // form pins Bucket to "fresh" for per-lead coupons). Leave it alone rather
    // than offering a sheet that would let the user edit around the lock.
    if (select.disabled) return

    // Prevent native dropdown from opening
    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation()  // Stop other handlers from receiving this event

    // Blur the select to prevent iOS focus-triggered picker
    select.blur()

    // Only open our overlay on touchstart/mousedown, not on click/focus
    // (click fires after touchend, so we'd get double opens otherwise)
    if (event.type === "click" || event.type === "focus") return

    this.openDropdown(select)
  }

  openDropdown(select) {
    // Close any existing dropdown
    this.closeDropdown()

    // Get title from select's label or data attribute
    const title = select.dataset.mobileDropdownTitle ||
                  this.getTitleFromLabel(select) ||
                  this.titleValue

    // Build and show overlay
    this.overlay = this.buildOverlay(select, title)
    document.body.appendChild(this.overlay)

    // Prevent body scroll while overlay is open
    document.body.style.overflow = "hidden"

    // The sheet is a modal dialog above everything else on the page, so it
    // owns the next Escape outright. It listens on WINDOW in the capture
    // phase: that is the first stop on every keydown's path, ahead of the
    // document-level capture listeners the filter cards keep while they are
    // open (filter_panel_controller, lib/filter_card_disclosure). Registration
    // order cannot help here — the panel's listener is already installed by
    // the time a select inside it opens the sheet — so only the phase can
    // (#14692: otherwise one Escape closes the sheet AND the panel behind it).
    window.addEventListener("keydown", this.boundCloseOnEscape, true)
    this.returnFocusTo = this.returnFocusTargetFor(select)

    // Focus first option for accessibility
    const firstOption = this.overlay.querySelector(".gl-mobile-dropdown-option")
    if (firstOption) firstOption.focus()
  }

  buildOverlay(select, title) {
    const overlay = document.createElement("div")
    overlay.className = "gl-mobile-dropdown-overlay"
    overlay.setAttribute("role", "dialog")
    overlay.setAttribute("aria-modal", "true")
    overlay.setAttribute("aria-label", title)

    // Backdrop click closes dropdown
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) this.closeDropdown()
    })

    const content = document.createElement("div")
    content.className = "gl-mobile-dropdown-content"

    // Header with title and close button
    const header = document.createElement("div")
    header.className = "gl-mobile-dropdown-header"
    header.innerHTML = `
      <span class="gl-mobile-dropdown-title">${this.escapeHtml(title)}</span>
      <button type="button" class="gl-mobile-dropdown-close" aria-label="Close" data-gl-tooltip="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
    `
    header.querySelector(".gl-mobile-dropdown-close").addEventListener("click", () => this.closeDropdown({ restoreFocus: true }))

    content.appendChild(header)

    // Options list
    const optionsList = document.createElement("div")
    optionsList.className = "gl-mobile-dropdown-options"
    optionsList.setAttribute("role", "listbox")
    if (select.multiple) optionsList.setAttribute("aria-multiselectable", "true")

    Array.from(select.options).forEach((option, index) => {
      // Skip disabled options
      if (option.disabled) return

      const optionEl = document.createElement("button")
      optionEl.type = "button"
      optionEl.className = "gl-mobile-dropdown-option"
      optionEl.setAttribute("role", "option")
      this.renderOptionState(optionEl, option)

      optionEl.addEventListener("click", () => {
        if (select.multiple) {
          this.toggleOption(select, option, optionEl)
        } else {
          this.selectOption(select, option.value, index)
        }
      })

      optionsList.appendChild(optionEl)
    })

    content.appendChild(optionsList)

    // A multi-select sheet stays open across taps, so it needs an explicit way
    // out. (Single-select closes on pick, so it needs no footer.)
    if (select.multiple) {
      content.appendChild(this.buildDoneFooter())
    }

    overlay.appendChild(content)

    return overlay
  }

  // Paints an option row to match its <option>'s current selected state. Split
  // out of buildOverlay so a multi-select toggle can repaint one row in place
  // without tearing down and rebuilding the whole sheet.
  renderOptionState(optionEl, option) {
    optionEl.setAttribute("aria-selected", option.selected ? "true" : "false")
    optionEl.classList.toggle("gl-mobile-dropdown-option--selected", option.selected)

    // Use option text, or the muted empty-value marker for a blank option
    const displayText = option.text || option.value
    optionEl.innerHTML = `
      <span class="gl-mobile-dropdown-option-text">${valueOrEmptyHtml(displayText)}</span>
      ${option.selected ? '<svg class="gl-mobile-dropdown-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>' : ''}
    `
  }

  buildDoneFooter() {
    const footer = document.createElement("div")
    footer.className = "gl-mobile-dropdown-footer"

    const done = document.createElement("button")
    done.type = "button"
    done.className = "gl-btn gl-btn-primary gl-mobile-dropdown-done"
    done.textContent = "Done"
    done.addEventListener("click", () => this.closeDropdown({ restoreFocus: true }))

    footer.appendChild(done)
    return footer
  }

  // Multi-select: flip just this option and leave the sheet open so the user can
  // keep picking. Assigning `select.value` here (the single-select path) would
  // clear every other selection, which is what made multi-value filters
  // impossible to set on a phone.
  toggleOption(select, option, optionEl) {
    option.selected = !option.selected
    this.renderOptionState(optionEl, option)
    this.notifyChange(select)
  }

  selectOption(select, value, index) {
    // Update the select value
    select.value = value
    select.selectedIndex = index

    this.notifyChange(select)

    // Close the dropdown
    this.closeDropdown({ restoreFocus: true })
  }

  notifyChange(select) {
    // Dispatch change event so other controllers can react
    const changeEvent = new Event("change", { bubbles: true })
    select.dispatchEvent(changeEvent)

    // Also dispatch input event for compatibility
    const inputEvent = new Event("input", { bubbles: true })
    select.dispatchEvent(inputEvent)
  }

  // Every explicit dismissal (Escape, the header X, Done, a single-select
  // pick) hands focus back to the control that opened the sheet, the way a
  // dialog returns to its trigger (A11Y-2). A backdrop tap chose its own
  // target and a teardown (disconnect, Turbo cache) has nowhere to return to,
  // so those leave focus alone.
  closeDropdown({ restoreFocus = false } = {}) {
    if (this.overlay) {
      this.overlay.remove()
      this.overlay = null
    }

    // Restore body scroll
    document.body.style.overflow = ""

    // Remove escape key listener (same phase it was added with)
    window.removeEventListener("keydown", this.boundCloseOnEscape, true)

    const target = this.returnFocusTo
    this.returnFocusTo = null
    if (restoreFocus && target?.isConnected && !target.disabled) target.focus({ preventScroll: true })
  }

  closeOnEscape(event) {
    if (event.key !== "Escape" || !this.overlay) return
    // Claim the key: nothing beneath the sheet may act on this Escape. The
    // filter cards honour defaultPrevented; stopPropagation covers any
    // document listener that does not.
    event.preventDefault()
    event.stopPropagation()
    this.closeDropdown({ restoreFocus: true })
  }

  beforeCache() {
    this.closeDropdown()
  }

  // A collapsed select's summary trigger is the only control the keyboard can
  // reach for that field; an uncollapsed select cannot take focus back here
  // (its focus handler blurs it on a phone to keep the native picker shut),
  // so there is nothing to return to and focus is left where the browser
  // puts it.
  returnFocusTargetFor(select) {
    const next = select.nextElementSibling
    if (next && next.classList.contains(SUMMARY_TRIGGER_CLASS)) return next
    return null
  }

  getTitleFromLabel(select) {
    // Try to find associated label by id
    const id = select.id
    if (id) {
      const label = document.querySelector(`label[for="${id}"]`)
      if (label) return label.textContent.trim()
    }

    // Fields titled by a section heading rather than a per-field <label> carry
    // their accessible name in aria-label (e.g. Licensed States on /profile).
    // Checked after label[for], which is the more authoritative source.
    const ariaLabel = select.getAttribute("aria-label")
    if (ariaLabel?.trim()) return ariaLabel.trim()

    // Try sibling label (common in .gl-mobile-filter-group structure)
    const filterGroup = select.closest(".gl-mobile-filter-group")
    if (filterGroup) {
      const siblingLabel = filterGroup.querySelector(".gl-mobile-filter-label")
      if (siblingLabel) return siblingLabel.textContent.trim()
    }

    // Try parent label (wrapping label)
    const parentLabel = select.closest("label")
    if (parentLabel) {
      // Get text content excluding the select itself
      const clone = parentLabel.cloneNode(true)
      const selectClone = clone.querySelector("select")
      if (selectClone) selectClone.remove()
      return clone.textContent.trim()
    }

    return null
  }

  // Phone-sized viewports get the sheet for every select it recognises.
  isMobile() {
    return isMobileSheetViewport(this.mobileBreakpointValue)
  }

  // Above that breakpoint the sheet normally stays out of the way — a tablet's
  // native picker is fine. The exception is a select that select2_manager has
  // COLLAPSED behind a summary trigger: there is no other usable control (the
  // select is pointer-events:none / tabindex -1), so refusing the tap would
  // leave a dead field. That is the tablet gap (#9241), and it is also why a
  // rotation past the breakpoint cannot strand a collapsed select.
  shouldHandle(select) {
    if (this.isMobile()) return true

    return select.classList.contains(SHEET_SELECT_CLASS) && isTouchPrimary()
  }

  escapeHtml(text) {
    return _escapeHtml(text)
  }
}
